import React from 'react';

import { readStoredUserSummary } from '../../../infrastructure/session/browserAuthStore';

import { useActiveWorkspace } from './useActiveWorkspace';
import { useEnsureFreshWorkspaceSummary } from './useEnsureFreshWorkspaceSummary';
import { useEnsureWorkspaceFeatureFlags } from './useEnsureWorkspaceFeatureFlags';
import { useWorkspaceVisibility } from './useWorkspaceVisibility';
import { PendingApprovalLock } from './PendingApprovalLock';

const readWorkspaceName = (workspaceId: string | null): string | null => {
  if (!workspaceId) return null;
  const summary = readStoredUserSummary<any>();
  const lists = [
    summary?.workspaces,
    summary?.data?.workspaces,
    summary?.supporting,
    summary?.data?.supporting
  ];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    const match = list.find(
      (ws: any) => String(ws?.id) === String(workspaceId)
    );
    if (match) return match?.workspace_name || match?.name || null;
  }
  return null;
};

// Wraps a route's element so that switching the active workspace via URL
// (e.g. /dashboard/A → /dashboard/B) forces a clean unmount + remount of
// the wrapped subtree. The `key=` on the React.Fragment is the load-
// bearing line — without it, React keeps the previous workspace's
// component mounted and its internal state (caches, fetched data,
// scroll position) leaks into the new workspace, which is the "Zaylan
// header on a CBH route" class of bug.
//
// The key combines workspaceId AND effectivePersona so that when the
// user's effective access changes WITHOUT the workspaceId changing
// (impersonation start, impersonation end, impersonation expiry), the
// subtree still unmounts. Previously, ending impersonation while on a
// budget page left the budget data in the React tree even though the
// sidebar correctly collapsed to the user's real visible_sections —
// a stale-page access-control bug.
//
// Also refreshes me/summary on workspace-id change so newly-added
// memberships and updated visible_sections appear without a hard reload.
//
// Routes without a workspace id in URL (auth pages, onboarding, etc.)
// receive a stable key ('no-workspace') so the layout is a no-op for
// them — the Fragment stays mounted across nav between non-workspace
// pages.

type WorkspaceLayoutProps = {
  children: React.ReactNode;
};

export const WorkspaceLayout = ({ children }: WorkspaceLayoutProps) => {
  const { workspaceId } = useActiveWorkspace();
  const { effectivePersona, isPendingApproval } = useWorkspaceVisibility();
  useEnsureFreshWorkspaceSummary(workspaceId);
  // Resolve feature flags for the VIEWED workspace (not the persisted active
  // one) so workspace-scoped overrides gate the UI correctly. See the hook.
  useEnsureWorkspaceFeatureFlags(workspaceId);
  const subtreeKey = workspaceId
    ? `${workspaceId}::${effectivePersona || 'none'}`
    : 'no-workspace';
  return (
    <React.Fragment key={subtreeKey}>
      {children}
      {/* Self-service volunteer/contributor awaiting owner approval: the
          persona dashboard renders above, this locks it until approved. */}
      {isPendingApproval ? (
        <PendingApprovalLock workspaceName={readWorkspaceName(workspaceId)} />
      ) : null}
    </React.Fragment>
  );
};
